import ButtonIcon from "components/button-icon";
import { useEffect, useState } from "react";
import { getAlterableItems, getButtonIcon, getButtonTextContent } from "./utils";
import { ModalConfirmAction } from "components/modal-confirm-action";
import { useOrdersContext } from "features/orders/context";
import { IOrder } from "types/orders/order";
import intl from "react-intl-universal";
import { useGlobalContext } from "hooks/useGlobalContext";

export interface IOrdersActionButtonProps {
  actionType: "accept" | "refuse";
  refresh: (newFilter?: any) => Promise<void>;
}

interface IActionButtonContent {
  buttonText: string;
  modalConfirmationTitle: string;
  modalConfirmationMessage: string;
  icon: JSX.Element;
}

const OrdersActionButton = ({ actionType, refresh }: IOrdersActionButtonProps) => {
  const { setLoading, checkbox, items } = useOrdersContext();
  const { startOperation } = useGlobalContext();
  const [actionButtonContent, setActionButtonContent] = useState<IActionButtonContent | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [alterableItems, setAlterableItems] = useState<IOrder[] | null>(null);

  const handleOpenConfirmModal = () => {
    setIsConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  const handleConfirmAction = () => {
    try {
      setLoading(true);
      const action = actionType;
      const requests = alterableItems?.map((x) => ({
        API: `orders/${x.Id}:${action}`,
        method: "post",
        body: x,
      }));
      const text = intl.get("orders.operationText03");
      startOperation(requests, text, refresh);
    } finally {
      setLoading(false);
      handleCloseConfirmModal();
    }
  };

  useEffect(() => {
    const _buttonTextContent = getButtonTextContent(actionType);
    const _buttonIcon = getButtonIcon(actionType);
    setActionButtonContent({
      icon: _buttonIcon,
      ..._buttonTextContent,
    });
  }, [actionType]);

  useEffect(() => {
    const selectedItems = items.filter((item: IOrder) => checkbox.includes(item.Id));
    const _alterableItems = getAlterableItems(actionType, selectedItems);
    setAlterableItems(_alterableItems);
  }, [checkbox]);

  return (
    <>
      {actionButtonContent && (
        <div className="btn-content" title={actionButtonContent.buttonText}>
          <ButtonIcon
            buttonIcon={actionButtonContent.icon}
            buttonText={actionButtonContent.buttonText}
            onClick={handleOpenConfirmModal}
            style={{ width: "160px" }}
            disabled={alterableItems?.length == 0}
            data-testid="action-button"
          />
        </div>
      )}
      {isConfirmModalOpen && actionButtonContent && (
        <ModalConfirmAction
          isOpen={isConfirmModalOpen}
          handleConfirm={handleConfirmAction}
          handleClose={handleCloseConfirmModal}
          title={actionButtonContent.modalConfirmationTitle}
          textMessage={actionButtonContent.modalConfirmationMessage}
        />
      )}
    </>
  );
};

export default OrdersActionButton;
